import * as React from 'react';

interface AnimatedNumberProps {
    value: number;
    // how the number is written - the same formatting the chart used before (currency symbol, decimals...)
    format: (value: number) => React.ReactNode;
    durationMilliseconds?: number;
}

const easeOutCubic = (progress: number) => 1 - Math.pow(1 - progress, 3);

const prefersReducedMotion = (): boolean =>
    typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Display only: counts up (or down) from the number it was showing to the new one, so a period switch feels alive instead of
// the figures just jumping. The value, its formatting and any click handler on the parent are the chart's own.
// It starts from 0 when it first mounts: switching a group or period swaps the chart to its skeleton while the data reloads, so
// the numbers are mounted fresh with the new data and would otherwise appear without any motion.
export const AnimatedNumber: React.FC<AnimatedNumberProps> = ({ value, format, durationMilliseconds = 800 }) => {
    const [displayedValue, setDisplayedValue] = React.useState(0);
    const shownValueRef = React.useRef(0);

    React.useEffect(() => {
        const startValue = shownValueRef.current;
        if (startValue === value || prefersReducedMotion() || typeof window.requestAnimationFrame !== 'function') {
            shownValueRef.current = value;
            setDisplayedValue(value);
            return undefined;
        }
        let frameId = 0;
        const startTime = performance.now();
        const step = (now: number) => {
            const progress = Math.min(1, (now - startTime) / durationMilliseconds);
            const nextValue = startValue + (value - startValue) * easeOutCubic(progress);
            shownValueRef.current = nextValue;
            setDisplayedValue(nextValue);
            if (progress < 1) {
                frameId = window.requestAnimationFrame(step);
            } else {
                shownValueRef.current = value;
                setDisplayedValue(value);
            }
        };
        frameId = window.requestAnimationFrame(step);
        return () => window.cancelAnimationFrame(frameId);
    }, [value, durationMilliseconds]);

    return <>{format(displayedValue)}</>;
};
